import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin } from 'lucide-react';
import Footer from '../components/Footer';
import axios from 'axios';
import { toast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [contactInfo, setContactInfo] = useState({
    title: 'Contact Us',
    description: 'Have questions? We\'d love to hear from you.',
    email: '',
    phone: '',
    address: ''
  });
  const [loadingInfo, setLoadingInfo] = useState(true);

  useEffect(() => {
    fetchContactInfo();
  }, []);

  const fetchContactInfo = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/contact-info`);
      if (response.data) {
        setContactInfo(response.data);
      }
    } catch (error) {
      console.error('Error fetching contact info:', error);
    } finally {
      setLoadingInfo(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Get user info from localStorage if available
      const token = localStorage.getItem('token');
      const userStr = localStorage.getItem('user');
      const user = userStr ? JSON.parse(userStr) : null;
      
      const submitData = {
        ...formData,
        subject: formData.subject || 'Contact Form Submission',
        category: formData.category || 'General',  // Default category for contact form
        user_id: user?.user_id || user?.id,
        user_role: user?.role
      };
      
      const response = await axios.post(`${BACKEND_URL}/api/support/submit`, submitData, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      
      if (response.data.success) {
        setIsSuccess(true);
        setFormData({ name: '', email: '', phone: '', message: '' });
        
        toast({
          title: "Message Sent!",
          description: "Thank you for contacting us. We'll get back to you soon.",
          variant: "default"
        });
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast({
        title: "Error",
        description: "Failed to send message. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-red-50 to-gray-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        <div className="container mx-auto px-4 py-12">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 max-w-2xl mx-auto text-center">
            <div className="mb-6">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-3xl font-bold text-gray-800 mb-3">Thank You!</h2>
              <p className="text-lg text-gray-600 mb-6">
                Your message has been received. We appreciate you contacting us and will get back to you as soon as possible.
              </p>
            </div>
            
            <div className="space-y-3">
              <button
                onClick={() => setIsSuccess(false)}
                className="bg-gradient-to-r from-red-600 to-red-800 text-white px-8 py-3 rounded-lg hover:shadow-lg transition-shadow font-semibold"
              >
                Send Another Message
              </button>
              <br />
              <Link
                to="/"
                className="inline-block text-red-700 hover:text-red-800 font-medium"
              >
                ← Back to Home
              </Link>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-red-50 to-gray-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <div className="container mx-auto px-4 py-12">
        <Link to="/" className="text-red-700 hover:text-red-800 mb-4 inline-block">
          ← Back to Home
        </Link>
        
        <h1 className="text-4xl font-bold mb-6 bg-gradient-to-r from-red-600 to-red-800 bg-clip-text text-transparent">
          {contactInfo.title || 'Contact Us'}
        </h1>
        
        {/* Contact Info Cards */}
        {!loadingInfo && (contactInfo.email || contactInfo.phone || contactInfo.address) && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {contactInfo.email && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 hover:shadow-xl transition-shadow">
                <div className="flex items-center justify-center w-12 h-12 bg-red-100 rounded-full mb-4 mx-auto">
                  <Mail className="w-6 h-6 text-red-700" />
                </div>
                <h3 className="font-semibold text-gray-800 text-center mb-2">Email</h3>
                <a 
                  href={`mailto:${contactInfo.email}`}
                  className="text-red-700 hover:text-red-800 text-sm text-center block break-all"
                >
                  {contactInfo.email}
                </a>
              </div>
            )}
            
            {contactInfo.phone && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 hover:shadow-xl transition-shadow">
                <div className="flex items-center justify-center w-12 h-12 bg-green-100 rounded-full mb-4 mx-auto">
                  <Phone className="w-6 h-6 text-green-600" />
                </div>
                <h3 className="font-semibold text-gray-800 text-center mb-2">Phone</h3>
                <a 
                  href={`tel:${contactInfo.phone}`}
                  className="text-green-600 hover:text-green-700 text-sm text-center block"
                >
                  {contactInfo.phone}
                </a>
              </div>
            )}
            
            {contactInfo.address && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 hover:shadow-xl transition-shadow">
                <div className="flex items-center justify-center w-12 h-12 bg-blue-100 rounded-full mb-4 mx-auto">
                  <MapPin className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="font-semibold text-gray-800 text-center mb-2">Address</h3>
                <p className="text-blue-600 text-sm text-center">
                  {contactInfo.address}
                </p>
              </div>
            )}
          </div>
        )}
        
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 max-w-2xl mx-auto">
          <h2 className="text-2xl font-bold mb-4">Get in Touch</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {contactInfo.description || 'Have questions? We\'d love to hear from you. Send us a message and we\'ll respond as soon as possible.'}
          </p>
          
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div>
              <label className="block text-sm font-medium mb-1">Name *</label>
              <input 
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:border-red-600 focus:ring-2 focus:ring-red-200 outline-none"
                placeholder="Your name"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Email *</label>
              <input 
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:border-red-600 focus:ring-2 focus:ring-red-200 outline-none"
                placeholder="your@email.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Phone (optional)</label>
              <input 
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:border-red-600 focus:ring-2 focus:ring-red-200 outline-none"
                placeholder="+1 (234) 567-8900"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Message *</label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:border-red-600 focus:ring-2 focus:ring-red-200 outline-none"
                rows={5}
                placeholder="Your message..."
              />
            </div>
            
            <button 
              type="submit"
              disabled={isSubmitting}
              className="bg-gradient-to-r from-red-600 to-red-800 text-white px-6 py-2 rounded-lg hover:shadow-lg transition-shadow disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
            >
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </button>
          </form>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ContactPage;
